import React, { useState, useEffect } from 'react';
import { first, isInteger, toNumber, startCase } from 'lodash';
import { useHistory } from 'react-router-dom';

import { setToken, setCookieIdentifierIfNotExists, getCookieIdentifier, doPost } from 'utils';
import { TextInput, MultiSelect, PrimaryBtn } from 'components/Shared';

import { ROOMS, MAX_ROOMS_ALLOWED_IN_LAYOUT, APARTMENT_TYPES } from '../constants';

import '../../AuthHomePage/home.scss';
import './style.scss';

function Questions() {
  const history = useHistory();
  const [buttonCaption, setButtonCaption] = useState('Get Estimate');
  const [disabled, setDisabled] = useState(false);
  const [formData, setFormData] = useState({
    require_modular_kitchen: 'yes',
    layout_type: '2bhk',
    rooms: new Set(['master bedroom']),
    carpet_area: 800,
  });
  const [error, setError] = useState(null);
  const errorMsg = error && error.msg;

  const calculateRemainingRooms = () => {
    const maxRooms = MAX_ROOMS_ALLOWED_IN_LAYOUT[formData.layout_type];
    const remainingRooms = maxRooms - formData.rooms.size;
    if (remainingRooms > 0) {
      setError({
        form: {
          rooms: `select ${remainingRooms} more room(s)`,
        },
      });
    } else {
      setError({
        form: {
          rooms: '',
        },
      });
    }
  };

  const updateFormData = ({ slug, value }) => {
    // Clear rooms if slug = layout_type
    setFormData(prevState => {
      let { rooms } = prevState;
      if (slug === 'layout_type') {
        rooms = new Set(['master bedroom']);
      }

      // This check is necessary
      if (slug === 'rooms' && MAX_ROOMS_ALLOWED_IN_LAYOUT[formData.layout_type] === rooms.size) {
        return { ...prevState };
      }

      // Check to provide feedback on incorrect Carpet Area
      if (slug === 'carpet_area') {
        if (!isInteger(toNumber(value)) || value === '') {
          setError({
            form: {
              carpetArea: 'Enter valid carpet area.',
            },
          });
        }
      }
      return {
        ...prevState,
        rooms,
        [slug]: slug === 'rooms' ? rooms.add(value) : value,
      };
    });
  };

  const handleSubmit = e => {
    e.preventDefault();

    // clear errors as things are clear!
    setError(null);

    if (formData.rooms.size !== MAX_ROOMS_ALLOWED_IN_LAYOUT[formData.layout_type]) {
      calculateRemainingRooms();
    }

    if (!isInteger(toNumber(formData.carpet_area)) || formData.carpet_area === '') {
      setError({
        form: {
          carpetArea: 'Enter valid carpet area.',
        },
      });
      return;
    }
    const data = {};

    Object.keys(formData).forEach(key => {
      if (formData[key] instanceof Set) {
        if (key === 'rooms') {
          data[key] = [...formData[key]];
        } else {
          data[key] = first([...formData[key]]);
        }
      } else {
        data[key] = formData[key];
      }
    });

    setButtonCaption('Processing...');
    setDisabled(true);

    const headers = {};
    if (getCookieIdentifier()) {
      headers['System-Identifier'] = getCookieIdentifier();
    }
    doPost({ url: 'queries', body: data, headers })
      .then(({ token, identifier }) => {
        // DON'T use UserContext.onLogin()
        // it causes page to reset to initial value
        // while the user is being redirected to /explore page
        setToken(token);
        setCookieIdentifierIfNotExists(identifier);
        history.push('/explore');
      })
      .catch(err => {
        console.error(err);

        setButtonCaption('Get Estimate');
        setDisabled(false);
        setError(err);
      });
  };
  const renderFormErrorDiv = message => {
    return <div className="form-error tw-text-sm tw-text-red-600">{message}</div>;
  };
  useEffect(() => {
    calculateRemainingRooms();
  }, [formData]);

  return (
    <div className="questionnaire container">
      {errorMsg && <p className="alert alert-danger errorMsg">{errorMsg}</p>}
      <div className="row box">
        <div className="col-12 col-sm-6 col-md-3 form-group border-radius-left">
          <label htmlFor="bhk">Select apartment type</label>
          <MultiSelect
            highlightSelection
            searchInput={false}
            single
            items={APARTMENT_TYPES.map(item => ({ label: item, value: item }))}
            onRemove={() => {}}
            onSelect={option => {
              updateFormData({ slug: 'layout_type', value: option.value });
            }}
            selectedItems={new Set([formData.layout_type])}
          />
        </div>
        <div className="col-12 col-sm-6 col-md-3 form-group">
          <label htmlFor="rooms">Select type of Bedrooms</label>
          <MultiSelect
            highlightSelection
            maxSelection={MAX_ROOMS_ALLOWED_IN_LAYOUT[formData.layout_type]}
            searchInput={false}
            items={ROOMS[formData.layout_type].map(room => ({
              label: startCase(room),
              value: room,
            }))}
            onRemove={option => {
              const { rooms: existingRooms } = formData;
              if (!option.value.match('master bedroom')) {
                existingRooms.delete(option.value);
              }
              setFormData({ ...formData, rooms: existingRooms });
            }}
            onSelect={option => {
              updateFormData({ slug: 'rooms', value: option.value });
            }}
            selectedItems={formData.rooms}
            restrictedValues={new Set(['master bedroom'])}
          />
          {error && error.form.rooms && renderFormErrorDiv(error.form.rooms)}
        </div>
        <div className="col-12 col-sm-6 col-md-3 form-group">
          <label htmlFor="require_modular_kitchen">Need a modular kitchen?</label>
          <MultiSelect
            highlightSelection
            searchInput={false}
            single
            items={['yes', 'no'].map(value => ({ label: value, value }))}
            onRemove={() => {}}
            onSelect={option => {
              updateFormData({
                slug: 'require_modular_kitchen',
                value: option.value,
              });
            }}
            selectedItems={new Set([formData.require_modular_kitchen])}
          />
        </div>
        <div className="col-12 col-sm-6 col-md-3 form-group border-right-0 border-radius-right">
          <label htmlFor="carpet_area">Carpet area in sqft.</label>
          <TextInput
            classes="tw-w-full"
            value={formData.carpet_area}
            placeholder="Enter area sqft"
            onChange={value => updateFormData({ slug: 'carpet_area', value })}
          />
          {error && error.form.carpetArea && renderFormErrorDiv(error.form.carpetArea)}
        </div>
      </div>
      <div className="row">
        <div className="button-container text-center">
          <PrimaryBtn
            onClick={handleSubmit}
            text={buttonCaption}
            disabled={disabled}
            className="tw-text-base"
          />
        </div>
      </div>
    </div>
  );
}

export { Questions };
